// author: Caleb Yearsley (caleb.yearsley@tecuity.com)
// date: 2019-08-12

import Parser from "html-react-parser";
import * as React from "react";
import ReactQuill from "react-quill";

import { notification, Button, Card, Input, Popconfirm, Spin } from "antd";
import { ContentSection } from "lib/data";
import { IBaseProps } from "lib/data/interfaces";
import { UtilityService } from "lib/services";
import { cloneDeep } from "lodash";
import "./EditableContentSection.less";

interface IContentSectionProps extends IBaseProps {
	onSave: (contentSection: ContentSection) => Promise<boolean>;
	onAfterSave: () => void;
	onSectionDelete: (contentSection: ContentSection) => void;
	contentSection: ContentSection;
}

interface IContentSectionState {
	isEditing: boolean;
	modifiedContentSection: ContentSection;
	isSaving: boolean;
}

export class EditableContentSection extends React.Component<IContentSectionProps, IContentSectionState> {

	public constructor(props: IContentSectionProps) {

		super(props);

		this.state = {
			isEditing: false,
			modifiedContentSection: props.contentSection,
			isSaving: false
		};
	}

	public componentWillReceiveProps(props: IContentSectionProps) {

		this.setState({
			modifiedContentSection: props.contentSection
		});
	}

	public render() {

		let cardTitle: React.ReactNode;

		if (this.props.contentSection.title || this.state.isEditing === true) {
			if (this.state.isEditing === true) {
				cardTitle = (
					<>
						Section Title:
						<div className="content-section-header-input">
							<Input
								defaultValue={this.props.contentSection.title}
								onChange={this.onTitleChange}
							/>
						</div>
					</>
				);
			}
			else {
				cardTitle = (
					<h4>
						{this.props.contentSection.title}
					</h4>
				);
			}
		}

		const deleteButton = (
			<Popconfirm
				title={(<>Are you sure delete this content section?<br />You cannot undo this action.</>)}
				onConfirm={this.onSectionDelete}
				okText="Yes"
				cancelText="No"
			>
				<Button
					key="delete"
					icon="delete"
					type="danger"
				>
					Delete Section
				</Button>
			</Popconfirm>
		);

		const className = `editable-content-section${this.props.className ? ` ${this.props.className}` : ""}`;

		return (
			<Spin
				spinning={this.state.isSaving}
				tip="Saving Section Content..."
			>
				<Card
					size="small"
					className={className}
					title={
						cardTitle !== undefined
						?
						(
							<div className="content-section-header">
								{cardTitle}
							</div>
						)
						:
						cardTitle
					}
					actions={
						this.state.isEditing === true
						?
						[
							<Button
								key="save"
								type="primary"
								onClick={this.onSectionSave}
							>
								Save
							</Button>,
							<Button
								key="cancel"
								icon="sync"
								onClick={this.onEditCancel}
							>
								Cancel
							</Button>,
							deleteButton
						]
						:
						[
							<Button
								key="edit"
								icon="edit"
								onClick={()=>this.setState({isEditing: true})}
							>
								Edit
							</Button>,
							deleteButton
						]
					}
				>
					{
						this.state.isEditing === true
						?
						(
							<ReactQuill
								value={this.state.modifiedContentSection.content}
								onChange={this.onWysiwygChange}
								className="content-section-wysiwyg"
							/>
						)
						:
						(
							Parser(this.props.contentSection.content)
						)
					}
				</Card>
			</Spin>
		);
	}

	/**
	 * Cancels an edit
	 */
	private onEditCancel = () => {

		this.setState({
			modifiedContentSection: cloneDeep(this.props.contentSection),
			isEditing: false
		});
	}

	/**
	 * Updates the modified content section content with the new value
	 * provided by the wysiwyg
	 * (Doesn't save)
	 */
	private onWysiwygChange = (newContent: string) => {

		const modifiedContentSection = cloneDeep(this.state.modifiedContentSection);
		modifiedContentSection.content = newContent;

		this.setState({modifiedContentSection});
	}

	/**
	 * Updates the modified content section's title in the state
	 * (Doesn't save)
	 */
	private onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		const modifiedContentSection = cloneDeep(this.state.modifiedContentSection);
		modifiedContentSection.title = event.target.value;

		this.setState({modifiedContentSection});
	}

	/**
	 * With the modified content section, calls the "On Save" method provided.
	 * On a failed save, provides feedback
	 */
	private onSectionSave = async () => {

		const contentText = this.state.modifiedContentSection.content.replace(/(<([^>]+)>)/ig, "");

		// Ensure that some content has been provided
		if (contentText.replace(/\s/g, "") !== "") {

			this.setState({isSaving: true});

			const successfulSave = await UtilityService.asyncTimeout(this.props.onSave(this.state.modifiedContentSection));

			this.setState({isSaving: false});

			if (successfulSave === true) {

				this.setState({
					isEditing: false,
					modifiedContentSection: this.props.contentSection
				});

				this.props.onAfterSave();
			}
			else {

				notification.error({
					placement: "bottomRight",
					message: "Content Save",
					description: "There was a problem while trying to save the content section"
				});
			}
		}
		else {

			notification.error({
				placement: "bottomRight",
				message: "Content Save",
				description: "You must specify content for the body of the section"
			});
		}
	}

	private onSectionDelete = () => {

		this.props.onSectionDelete(this.state.modifiedContentSection);
	}
}
